import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Building2, Check, Save, Store, Upload } from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

/**
 * Billing details — yours, and each vendor's.
 *
 * These two live together because they are the same job: making sure a document
 * has real names and numbers on it rather than a display nickname. A document
 * generated before this is filled in is technically valid and practically
 * useless to an accountant.
 *
 * The self-billing toggle is here rather than on the builder, because agreeing
 * to self-billing is a conversation with the vendor, not a per-document choice.
 */

export default function DocumentSettingsPage() {
  const navigate = useNavigate();
  const { authAxios, isAdmin, isSubAdmin } = useAuth();
  const canEditOrg = isAdmin || isSubAdmin;

  const [types, setTypes] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [org, setOrg] = useState({
    org_name: "", org_address: "", org_tax_id: "", org_email: "",
    default_doc_type: "purchase_order",
  });
  const [selectedVendor, setSelectedVendor] = useState("");
  const [business, setBusiness] = useState({
    legal_name: "", business_address: "", tax_id: "", payment_terms: "",
    self_billing_agreed: false,
  });
  const [loading, setLoading] = useState(true);
  const [savingOrg, setSavingOrg] = useState(false);
  const [savingVendor, setSavingVendor] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const api = authAxios();
      const [t, v] = await Promise.all([
        api.get("/document-types"),
        api.get("/vendors"),
      ]);
      setTypes(t.data.types);
      setVendors(v.data);
      setOrg((prev) => ({ ...prev, default_doc_type: t.data.default }));
    } catch {
      toast.error("Could not load billing settings");
    } finally {
      setLoading(false);
    }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const pickVendor = (vendorId) => {
    setSelectedVendor(vendorId);
    const v = vendors.find((x) => x.vendor_id === vendorId);
    setBusiness({
      legal_name: v?.legal_name || "",
      business_address: v?.business_address || "",
      tax_id: v?.tax_id || "",
      payment_terms: v?.payment_terms || "",
      self_billing_agreed: !!v?.self_billing_agreed,
    });
  };

  const saveOrg = async () => {
    setSavingOrg(true);
    try {
      const api = authAxios();
      await api.put("/settings/billing", org);
      toast.success("Billing details saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSavingOrg(false);
    }
  };

  const saveVendor = async () => {
    if (!selectedVendor) return;
    setSavingVendor(true);
    try {
      const api = authAxios();
      await api.put(`/vendors/${selectedVendor}/business`, business);
      toast.success("Vendor details saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    } finally {
      setSavingVendor(false);
    }
  };

  const uploadLogo = async (file) => {
    if (!file || !selectedVendor) return;
    setUploading(true);
    try {
      const api = authAxios();
      const body = new FormData();
      body.append("file", file);
      await api.post(`/vendors/${selectedVendor}/logo`, body);
      toast.success("Logo uploaded");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload the logo");
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4 md:p-8 w-full space-y-3">
        <div className="h-20 bg-muted rounded-xl animate-pulse" />
        <div className="h-64 bg-muted rounded-xl animate-pulse" />
      </div>
    );
  }

  const vendor = vendors.find((v) => v.vendor_id === selectedVendor);

  return (
    <div className="p-4 md:p-8 w-full space-y-5 pb-24">
      <Button variant="ghost" size="sm" className="-ml-2 gap-1.5"
              onClick={() => navigate("/documents")} data-testid="settings-back">
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Documents
      </Button>

      <h1 className="text-2xl font-bold text-foreground">Billing details</h1>

      {canEditOrg && (
        <Card>
          <CardContent className="p-5 space-y-4">
            <h2 className="font-semibold text-foreground flex items-center gap-1.5">
              <Building2 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
              Your organisation
            </h2>
            <p className="text-xs text-muted-foreground">
              These appear as the buyer on every document.
            </p>

            <div className="space-y-1.5">
              <Label htmlFor="orgname">Legal name</Label>
              <Input id="orgname" className="h-11" value={org.org_name}
                     onChange={(e) => setOrg({ ...org, org_name: e.target.value })}
                     data-testid="org-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="orgaddr">Address</Label>
              <Textarea id="orgaddr" rows={2} value={org.org_address}
                        onChange={(e) => setOrg({ ...org, org_address: e.target.value })}
                        data-testid="org-address" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="orgtax">Tax id</Label>
                <Input id="orgtax" className="h-11" value={org.org_tax_id}
                       onChange={(e) => setOrg({ ...org, org_tax_id: e.target.value })}
                       data-testid="org-tax" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="orgemail">Finance email</Label>
                <Input id="orgemail" type="email" className="h-11" value={org.org_email}
                       onChange={(e) => setOrg({ ...org, org_email: e.target.value })}
                       data-testid="org-email" />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Default document type</Label>
              <div className="flex flex-wrap gap-2">
                {types.map((t) => (
                  <Button key={t.id} size="sm" className="h-11"
                          variant={org.default_doc_type === t.id ? "default" : "outline"}
                          onClick={() => setOrg({ ...org, default_doc_type: t.id })}
                          data-testid={`default-${t.id}`}>
                    {t.title}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Only the starting choice — you pick the type on each document.
              </p>
            </div>

            <Button className="h-11 gap-1.5" onClick={saveOrg} disabled={savingOrg}
                    data-testid="save-org">
              <Save className="h-4 w-4" aria-hidden="true" />
              Save organisation details
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-5 space-y-4">
          <h2 className="font-semibold text-foreground flex items-center gap-1.5">
            <Store className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            Vendor business details
          </h2>

          <div className="space-y-1.5">
            <Label htmlFor="vpick">Vendor</Label>
            <select id="vpick" value={selectedVendor}
                    onChange={(e) => pickVendor(e.target.value)}
                    className="w-full h-11 rounded-lg border border-border bg-card
                               text-foreground px-3 text-sm"
                    data-testid="settings-vendor">
              <option value="">Choose a vendor…</option>
              {vendors.map((v) => (
                <option key={v.vendor_id} value={v.vendor_id}>
                  {v.name}{v.legal_name ? "" : " — no details yet"}
                </option>
              ))}
            </select>
          </div>

          {selectedVendor && (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="legal">Legal name</Label>
                <Input id="legal" className="h-11" value={business.legal_name}
                       placeholder={vendor?.name}
                       onChange={(e) => setBusiness({ ...business, legal_name: e.target.value })}
                       data-testid="vendor-legal-name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="baddr">Business address</Label>
                <Textarea id="baddr" rows={2} value={business.business_address}
                          onChange={(e) =>
                            setBusiness({ ...business, business_address: e.target.value })}
                          data-testid="vendor-address" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="btax">Tax id</Label>
                  <Input id="btax" className="h-11" value={business.tax_id}
                         onChange={(e) => setBusiness({ ...business, tax_id: e.target.value })}
                         data-testid="vendor-tax" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="bterms">Payment terms</Label>
                  <Input id="bterms" className="h-11" placeholder="Net 14"
                         value={business.payment_terms}
                         onChange={(e) =>
                           setBusiness({ ...business, payment_terms: e.target.value })}
                         data-testid="vendor-terms" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="logo">Logo</Label>
                {vendor?.logo_path ? (
                  <p className="text-sm text-emerald-600 flex items-center gap-1.5"
                     data-testid="vendor-logo-present">
                    <Check className="h-4 w-4" aria-hidden="true" />Logo uploaded
                  </p>
                ) : (
                  <Input id="logo" type="file" accept="image/png,image/jpeg,image/webp"
                         className="h-11 pt-2" disabled={uploading}
                         onChange={(e) => uploadLogo(e.target.files?.[0])}
                         data-testid="vendor-logo" />
                )}
                <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                  <Upload className="h-3 w-3" aria-hidden="true" />
                  {uploading ? "Uploading…" : "Appears on self-billed invoices. PNG, JPEG or WEBP."}
                </p>
              </div>

              {/* Agreeing to self-billing is a conversation with the vendor, not
                  a per-document toggle — so it lives here. */}
              <div className="rounded-lg border border-border p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-medium text-foreground text-sm">
                      Self-billing agreed
                    </p>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      Only turn this on once this vendor has agreed in writing that you
                      may raise invoices on their behalf. Without it, you can still send
                      them purchase orders.
                    </p>
                  </div>
                  <Button size="sm" className="h-11 shrink-0"
                          variant={business.self_billing_agreed ? "default" : "outline"}
                          onClick={() => setBusiness({
                            ...business,
                            self_billing_agreed: !business.self_billing_agreed,
                          })}
                          aria-pressed={business.self_billing_agreed}
                          data-testid="vendor-self-billing">
                    {business.self_billing_agreed ? "Agreed" : "Not agreed"}
                  </Button>
                </div>
              </div>

              <Button className="h-11 gap-1.5" onClick={saveVendor} disabled={savingVendor}
                      data-testid="save-vendor">
                <Save className="h-4 w-4" aria-hidden="true" />
                Save vendor details
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
